import { AlertColor, Box, CircularProgress, useTheme } from '@mui/material';
import React, { FC, useCallback, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import strings from '../../../../../../common/Translation/Translate';
import Notification from '../../../../../../components/Notification';
import PrimaryButton from '../../../../../../components/Button/PrimaryButton';
import axiosInstance from '../../../../../../utils/axios';
import { URLS } from '../../../../../../utils/constants/urls';
import { AddUnavailabilityDateModal } from './AddUnavailabilityDateModal';
import { UnavailabilityCalendar } from './UnavailabilityCalendar';
import {
  buildGeneralRequestFromPageResponse,
  UnavailabilityDateRange,
  unavailabilityToFormValues,
} from './unavailabilityDateUtils';
import { UnavailabilityDateList } from './UnavailabilityDateList';

type UnavailabilityProps = {
  pageResponse: any;
  getPageData: (id: number) => void;
  dates: UnavailabilityDateRange[];
  setDates: React.Dispatch<React.SetStateAction<UnavailabilityDateRange[]>>;
};

const Unavailability: FC<UnavailabilityProps> = ({ pageResponse, getPageData, dates, setDates }) => {
  const { id } = useParams() as { id?: string };
  const theme = useTheme();
  const queryStings = useLocation().search;
  const queryStingId = new URLSearchParams(queryStings).get('id');
  const configId = id || queryStingId;

  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [editIndex, setEditIndex] = useState<number | null>(null);
  const [modalValues, setModalValues] = useState<Record<string, unknown>>({});
  const [message, setMessage] = useState<{
    display: boolean;
    severity: AlertColor | null;
    message: string;
  }>({
    display: false,
    severity: null,
    message: '',
  });

  const closeNotification = (value: boolean) => {
    setMessage({ ...message, display: value });
  };

  const closeModal = () => {
    setModalOpen(false);
    setIsEdit(false);
    setEditIndex(null);
    setModalValues({});
  };

  const openAddModal = () => {
    setIsEdit(false);
    setEditIndex(null);
    setModalValues({});
    setModalOpen(true);
  };

  const openEditModal = (index: number) => {
    setIsEdit(true);
    setEditIndex(index);
    setModalValues(unavailabilityToFormValues(dates[index]));
    setModalOpen(true);
  };

  const handleModalSave = (range: UnavailabilityDateRange) => {
    const next = [...dates];
    if (editIndex !== null) {
      next[editIndex] = { ...next[editIndex], ...range };
    } else {
      next.push(range);
    }
    setDates(next);
    closeModal();
  };

  const handleDelete = (index: number) => {
    setDates(dates.filter((_, i) => i !== index));
  };

  const handleSubmit = useCallback(async () => {
    if (!configId) return;
    try {
      setLoading(true);
      const payload = buildGeneralRequestFromPageResponse(pageResponse, dates);
      const { status } = await axiosInstance.put(
        URLS.venueConfigsUpdateGeneral(Number(configId)),
        payload,
      );
      if (status === 200) {
        setMessage({
          display: true,
          severity: 'success',
          message: strings.common_update_message,
        });
        getPageData(Number(configId));
      }
    } catch (e: any) {
      setMessage({
        display: true,
        severity: 'error',
        message: e.response?.data?.message ?? e.message,
      });
    } finally {
      setLoading(false);
    }
  }, [configId, pageResponse, dates, getPageData]);

  return (
    <>
      {message.display && (
        <Notification
          isOpen={message.display}
          message={message.message}
          severity={message.severity as AlertColor}
          closeNotification={closeNotification}
        />
      )}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap: 3,
          alignItems: 'stretch',
          pb: 2,
        }}>
        <Box sx={{ flex: { md: '0 0 30%' }, width: { xs: '100%', md: '30%' } }}>
          <UnavailabilityCalendar dates={dates} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <UnavailabilityDateList
            dates={dates}
            onAdd={openAddModal}
            onEdit={openEditModal}
            onDelete={handleDelete}
          />
        </Box>
      </Box>
      <Box sx={{ mt: 3 }}>
        <PrimaryButton
          disabled={loading}
          sx={{
            minWidth: '100px',
            '&.defaultButton': {
              background: theme.palette.primary.main,
              color: theme.palette.primary.contrastText,
            },
          }}
          fullWidth={false}
          variant="contained"
          onClick={handleSubmit}>
          {loading && <CircularProgress color="inherit" sx={{ ml: 1 }} size={20} />}
          {strings.common_button_save}
        </PrimaryButton>
      </Box>
      <AddUnavailabilityDateModal
        open={modalOpen}
        isEdit={isEdit}
        initialValues={modalValues}
        onClose={closeModal}
        onSave={handleModalSave}
      />
    </>
  );
};

export default Unavailability;
